/**
 * Splitting a millisecond duration into days / hours / minutes, and putting it back together.
 *
 * The two timing knobs on this screen are naturally measured in days — a quiz stays visible for two
 * days after full time, a daily quiz shows its results for one — and a single "minutes" box made an
 * admin do the arithmetic (2880) and made a typo (288) indistinguishable from a deliberate value.
 *
 * Kept pure and unit-tested rather than inlined in the component, because the round trip is the part
 * that has to hold: whatever is stored must come back as the same three numbers, and those three
 * numbers must go back to the same stored value. A drift here silently rewrites a config an admin
 * only opened to look at.
 */

const MINUTE_MS = 60 * 1000;
const HOUR_MINUTES = 60;
const DAY_MINUTES = 24 * 60;

/** Empty rather than zero, so a field with no stored value does not read as a deliberate 0. */
const BLANK = { days: '', hours: '', minutes: '' };

/**
 * Milliseconds to `{ days, hours, minutes }`.
 *
 * A missing or unusable value gives blank fields; a real 0 gives three zeros, because zero IS
 * meaningful for `dailyQuizResultsWindowMs` ("results go the moment it closes") and must not be
 * displayed as "unset".
 *
 * Sub-minute precision is rounded away. Nothing on this screen can express it, so keeping it would
 * mean a stored 90s coming back as 2min and saving as 120s — a change the admin never asked for and
 * cannot see. Rounding at the boundary makes that visible instead of latent.
 */
export const msToParts = (ms) => {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms < 0) return { ...BLANK };

  const totalMinutes = Math.round(ms / MINUTE_MS);
  return {
    days: Math.floor(totalMinutes / DAY_MINUTES),
    hours: Math.floor((totalMinutes % DAY_MINUTES) / HOUR_MINUTES),
    minutes: totalMinutes % HOUR_MINUTES
  };
};

/** One field of the form to a number. Blank, negative and nonsense all read as 0. */
const fieldToNumber = (value) => {
  if (value === '' || value === null || value === undefined) return 0;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.floor(parsed);
};

/**
 * `{ days, hours, minutes }` back to milliseconds.
 *
 * Deliberately does NOT require the parts to be normalised: 36 hours is a perfectly clear thing for
 * an admin to type and means a day and a half. Normalising the fields as they type would move the
 * cursor out from under them, so the form holds what was typed and this does the arithmetic.
 */
export const partsToMs = (parts) => {
  const { days, hours, minutes } = parts ?? {};
  const totalMinutes =
    fieldToNumber(days) * DAY_MINUTES +
    fieldToNumber(hours) * HOUR_MINUTES +
    fieldToNumber(minutes);
  return totalMinutes * MINUTE_MS;
};

/**
 * Whether the admin has actually entered a duration — any one of the three fields filled in.
 *
 * The counterpart to `partsToMs`, and the reason it exists: `partsToMs` maps blank to 0 because a
 * blank box has to arithmetic as nothing, but a caller sending that 0 to the server is asserting a
 * zero-length duration, which is a completely different claim from "the admin did not fill this in".
 * Conflating the two is what disabled every Game Day quiz on the platform: the Poll/Quiz settings
 * form sent both timings on every save, so opening it to reorder the precedence list wrote
 * `quizPostMatchVisibilityMs: 0` — and at zero a quiz is dropped from the Team Channel and from the
 * Play to Win list the moment its match reads as ended.
 *
 * So: ask this before sending. Blank means leave the stored value alone.
 */
export const hasParts = (parts) => {
  const { days, hours, minutes } = parts ?? {};
  return [days, hours, minutes].some(
    (value) => value !== '' && value !== null && value !== undefined
  );
};

/** The three fields, in the order they are shown. */
export const DURATION_FIELDS = [
  { key: 'days', label: 'days' },
  { key: 'hours', label: 'hours' },
  { key: 'minutes', label: 'mins' }
];
